import {Injectable} from '@angular/core';
import {AbstractControl, UntypedFormGroup, ValidationErrors, ValidatorFn} from "@angular/forms";

/**
 * Service with helper functions for form Validation
 */
@Injectable({
  providedIn: 'root'
})
export class ValidationService {
  /**
   * excact required length of a postal code
   */
  public static readonly POSTALCODE_LENGTH = 5;

  /**
   * Minimum Building year
   */
  public static readonly BUILDING_YEAR_MIN = 1900;
  public static readonly BUILDING_AREA_ONE_FAMILY = {min: 50, max: 350};
  public static readonly BUILDING_AREA_MULTI_FAMILY = {min: 150, max: 100000};

  /**
   * regexp for validatiing (german) date formats
   */
  public static readonly DATE_REGEXP = /^(0?[1-9]|[12][0-9]|3[01])[.](0?[1-9]|1[012])[.](19|20\d\d)$/;

  /**
   * Validates a zipcode/postal_code in german format
   * @param control
   */
  static postalCode(control: AbstractControl): ValidationErrors | null {
    const value = control.value + "";
    return value && value.length > 0 && parseInt(value) > 1000 && value.match(new RegExp("^[0-9]{" + ValidationService.POSTALCODE_LENGTH + "}$"))? null : {postalCode: false};
  }

  /**
   * Validates a input to a valid dateformat (dd.mm.yyyy or d.m.yyyy)
   * @param control
   */
  static dateFormat(control: AbstractControl): ValidationErrors | null {
    const isRealDate = str => {
      let [dd, mm, yyyy] = str.split(/[.]/);
      // cast to number
      dd = +dd;
      yyyy = +yyyy;
      mm = +mm;
      // js months are 0 based
      const mm0 = mm - 1;
      // normalise
      const date = new Date(yyyy, mm0, dd, 15, 0, 0, 0);
      return mm0 === date.getMonth() && dd === date.getDate() && yyyy === date.getFullYear();
    };
    return control && control.value && isRealDate(control.value) ? null : {dateFormat: false};
  }

  /**
   * Validates two datefields for their min and max diff in days. Returns an error if the diff is ouside the min/max range
   * @param field1
   * @param field2
   * @param min
   * @param max
   * @param postfixes
   */
  static dateRangeMinMax(field1: string, field2: string, min: number, max: number, postfixes: string[] = ['']): ValidatorFn {
    return (formGroup: UntypedFormGroup) => {
      for(let postfix of postfixes) {
        const field1Name = field1 + postfix;
        const field2Name = field2 + postfix;
        if (formGroup.controls[field1Name] && formGroup.controls[field2Name]) {
          // try if the values matches the
          const dateField1 = ValidationService.dateFromString(formGroup.controls[field1Name].value);
          const dateField2 = ValidationService.dateFromString(formGroup.controls[field2Name].value);

          if (dateField1 && dateField2) {
            const diffDays = Math.abs(dateField1.getTime() - dateField2.getTime()) / (1000 * 60 * 60 * 24) + 1;

            if (diffDays < min || diffDays > max) {
              // diff si invalid
              return {dateRangeMinMax: true};
            }
          }
        }
      }

      // everything is fine, of the fields are not theire, of they have no values
      return null;
    }
  }

  /**
   * Validates if a datefield is X days in the past.
   * @param field
   * @param minDays
   */
  static dateInPast(field: string, minDays: number = 0): ValidatorFn {
    return (formGroup: UntypedFormGroup) => {
      if(formGroup.controls[field]) {
        // try if the values matches the
        const dateField = ValidationService.dateFromString(formGroup.controls[field].value);

        if(dateField) {
          const now = new Date();
          const diffDays = (dateField.getTime() - now.getTime()) / (1000*60*60*24) ;

          if(diffDays > (minDays * -1)) {
            // diff si invalid
            return {dateInPast: true};
          }
        }
      }

      // everything is fine, of the fields are not theire, of they have no values
      return null;
    }
  }

  /**
   * try to transform a user given date to da Date object
   * @param dateString
   */
  static dateFromString(dateString: string): Date {
    const matchesfield = dateString?.match(ValidationService.DATE_REGEXP);
    if(matchesfield) {
      return new Date(parseInt(matchesfield[3]), parseInt(matchesfield[2]) - 1, parseInt(matchesfield[1]));
    }
    return null;
  }

  /**
   * Validates a building year. Number between YEAR_MIN and current year
   * @param control
   */
  static buildingYear(control: AbstractControl): ValidationErrors | null {
    const value = parseInt(control.value);
    return value >= ValidationService.BUILDING_YEAR_MIN && value <= (new Date()).getFullYear() ? null : {buildingYear: false};
  }

  /**
   * Validates a building year. Number between YEAR_MIN and current year or empty
   * @param control
   */
  static buildingYearOrEmpty(control: AbstractControl): ValidationErrors | null {
    const value = parseInt(control.value);
    return value >= ValidationService.BUILDING_YEAR_MIN && value <= (new Date()).getFullYear()
      || control.value === null || control.value === '' ? null : {buildingYear: false};
  }

  /**
   * Validates a building area for one/two-family houses
   * @param control
   */
  static buildingAreaOneFamilyHouse(control: AbstractControl): ValidationErrors | null {
    const value = parseInt(control.value);
    return value >= ValidationService.BUILDING_AREA_ONE_FAMILY.min && value <= ValidationService.BUILDING_AREA_ONE_FAMILY.max? null : {buildingArea: false};
  }

  /**
   * Validates a building area for multi-family houses
   * @param control
   */
  static buildingAreaMultiFamilyHouse(control: AbstractControl): ValidationErrors | null {
    const value = parseInt(control.value);
    return value >= ValidationService.BUILDING_AREA_MULTI_FAMILY.min && value <= ValidationService.BUILDING_AREA_MULTI_FAMILY.max? null : {buildingArea: false};
  }

  /**
   * Validates if the value is a valid percentage value between 0 and 100.
   * @param control
   */
  static percentage(control: AbstractControl): ValidationErrors | null {
    const value = parseInt(control.value);
    return value >= 0 && value <= 100? null : {percentage: false};
  }

  /**
   * Formvalidator to validate two datefields that should giva a range. Check the datediff.
   * @param field1
   * @param field2
   */
  static dateRange(field1: string, field2: string): ValidatorFn {

    return (formGroup: UntypedFormGroup) => {
      if(formGroup.controls[field1] && formGroup.controls[field2] && formGroup.controls[field1] instanceof Date && formGroup.controls[field2].value instanceof Date) {
        if((<Date>formGroup.controls[field1].value).getTime() >= (<Date>formGroup.controls[field2].value).getTime()) {
          formGroup.controls[field1].setErrors({dateRangeNegativeDiff: false});
          formGroup.controls[field2].setErrors({dateRangeNegativeDiff: false});
        }
      }

      return null;
    }
  }

  /**
   * Formvalidator to validate if the value of field 1 is smaller or equal to value of field 2.
   * Only field1 will be marked as wrong.
   * @param field1
   * @param field2
   */
  static smallerEqual(field1: string, field2: string): ValidatorFn {

    return (formGroup: UntypedFormGroup) => {
      // check if both values are there and set.
      if(formGroup.controls && formGroup.controls[field1]?.value && formGroup.controls[field2]?.value) {
        const value1 = typeof formGroup.controls[field1].value === "string"? formGroup.controls[field1].value.replace(/,/, '.') : formGroup.controls[field1].value;
        const value2 = typeof formGroup.controls[field2].value === "string"? formGroup.controls[field2].value.replace(/,/, '.') : formGroup.controls[field2].value;

        if((value1 * 1) > (value2 * 1)) {
          return {smallerEqual: false};
        }
      }

      return null;
    }
  }

  /**
   * Validates a date field for a minimum and maximum year. Returns an error if the year is outside the min/max range.
   */
  static dateYearMinMax = (min: number, max: number) => {
    return (control: AbstractControl) => {
      const dateField = ValidationService.dateFromString(control.value);
      if (dateField) {
        if (dateField.getFullYear() < min || dateField.getFullYear() > max) {
          // year is invalid
          return {dateYearMinMax: false};
        }
      }
      return null;
    };
  }

  /**
   * Validates a date field for a minimum and maximum. Returns an error if the year is outside the min/max range.
   * @param min german date format
   * @param max german date format
   */
  static dateBetween = (min: string, max: string) => {
    return (control: AbstractControl) => {
      const dateField = ValidationService.dateFromString(control.value);
      const minField = ValidationService.dateFromString(min);
      const maxField = ValidationService.dateFromString(max);
      if (dateField && minField && maxField) {
        if (dateField < minField || dateField > maxField) {
          // date is invalid
          return {dateBetween: false};
        }
      }
      return null;
    };
  }

  /**
   * Validates if the input value is > 0.
   * @param control
   */
  static greaterZero(control: AbstractControl): ValidationErrors | null {
    if(control.value === null || control.value === '') {
      // stop here, value is empty
      return null;
    }

    const value = parseFloat(control.value);
    return value > 0 ? null : {greaterZero: false}
  }

  /**
   * Check if the different invoice dates do not overlapp each other
   */
  public static validateInvoiceDates(): ValidatorFn {
    return (formGroup: UntypedFormGroup) => {
      let formFieldFromReference = "abrechnung_von";
      let formFieldToReference = "abrechnung_bis";

      for(let index = 1; index < 10; index++) {
        const formFieldFrom = "abrechnung_von" + index;
        const formFieldTo = "abrechnung_bis" + index;
        if(formGroup.get(formFieldFrom) && formGroup.get(formFieldTo)) {
          const dateAStart = ValidationService.dateFromString(formGroup.get(formFieldFrom).value);
          const dateAEnd = ValidationService.dateFromString(formGroup.get(formFieldTo).value);
          const dateBStart = ValidationService.dateFromString(formGroup.get(formFieldFromReference).value);
          const dateBEnd = ValidationService.dateFromString(formGroup.get(formFieldToReference).value);

          if((dateAStart >= dateBStart && dateAStart <= dateBEnd) || (dateAEnd >= dateBStart && dateAEnd <= dateBEnd)) {
            return {dateOverlapp: true}
          }

          // save current fields as references.
          formFieldFromReference = formFieldFrom;
          formFieldToReference = formFieldTo;
        }
        else {
          // stop here
          break;
        }
      }

      return null;
    }
  }

  /**
   * Validates a minimum building year via parameter.
   * @param minYear
   */
  public static minBuildingYear(minYear: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = parseInt(control.value, 10);
      return value >= minYear && value <= (new Date()).getFullYear() ? null : {minBuildingYear: false};
    }
  }

  /**
   * Converts a string into a float number replacing the comma with a dot
   * @param fieldValue
   */
  public static parseStringToFloat(fieldValue: string) {
    if(typeof fieldValue === "string" && fieldValue.includes(',')) {
      return parseFloat(fieldValue.replace(/,/, '.'));
    }
    return fieldValue;
  }

  /**
   *
   * Validates if input has a comma or a dot and returns an error
   * @param control
   */
  static allowIntegerOnly(control: AbstractControl): ValidationErrors | null {
    const value = control.value + "";
    return /[.,]/.test(value) ? {noInteger: false} : null;
  }

}
